// web-commands.ts — the PER-APP half of the web shape: maps ai_health_tracker_coach's Tauri
// data commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser
// WebStore. Plain CRUD is a few lines each; compute_signals is a faithful JS port of
// signals.rs (simple aggregation), so the web surface's recap/ask work too. The photo/label
// commands are NATIVE-ONLY (photo→calories goes through the Oriora brain, no vendor key on
// the device; label-scan uses Apple Vision OCR, native-only);
// on web they degrade so the UI's own fallbacks take over — the barcode path is plain
// client fetch and works everywhere.

import type { CommandHandler, WebStore } from "./web-runtime";
import { browserSearch, browserFetch } from "./web-search-browser";

const MEALS = "meals";
const WEIGHTS = "weights";

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}
const round1 = (v: number) => Math.round(v * 10) / 10;

function clampDate(s: unknown): string {
  const v = String(s ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : todayLocal();
}
function clampCalories(c: unknown): number {
  const v = Number(c ?? 0);
  if (!Number.isFinite(v) || v < 0) return 0;
  return Math.min(100000, Math.round(v));
}
function clampMacro(m: unknown): number | null {
  if (m === null || m === undefined || m === "") return null;
  const v = Number(m);
  if (!Number.isFinite(v) || v < 0) return null;
  return round1(Math.min(10000, v));
}
function clampSource(s: unknown): string {
  const v = String(s ?? "manual");
  return v === "manual" || v === "photo" || v === "label" || v === "barcode" ? v : "manual";
}
/** Mirrors the Rust clamp_slot: an unknown slot becomes "" rather than a guess. */
function clampSlot(v: unknown): string {
  return v === "breakfast" || v === "lunch" || v === "dinner" || v === "snack" ? v : "";
}

type MealRow = {
  id: string; name: string; eaten_on: string; calories: number;
  protein_g: number | null; carbs_g: number | null; fat_g: number | null;
  source: string; slot: string; note: string; created_at: string; updated_at: string;
};
type WeightRow = { id: string; measured_on: string; weight_kg: number; note: string; created_at: string };

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function dateToUtcDay(s: string): number {
  return Math.floor(Date.parse(`${s}T00:00:00Z`) / 86400000);
}
function utcDayToWeekdayIdx(day: number): number {
  // 1970-01-01 was a Thursday (idx 3 in Mon..Sun).
  return (((day + 3) % 7) + 7) % 7;
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  const sortedMeals = (): MealRow[] =>
    (db.all(MEALS) as MealRow[]).slice().sort((a, b) => {
      if (a.eaten_on !== b.eaten_on) return b.eaten_on.localeCompare(a.eaten_on);
      return String(b.created_at).localeCompare(String(a.created_at));
    });
  const sortedWeights = (): WeightRow[] =>
    (db.all(WEIGHTS) as WeightRow[]).slice().sort((a, b) => b.measured_on.localeCompare(a.measured_on));

  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Meals ─────────────────────────────────────────────────────────────────
    list_meals: ({ limit }) => {
      const rows = sortedMeals();
      const n = Number(limit);
      return Number.isFinite(n) && n > 0 ? rows.slice(0, n) : rows;
    },
    create_meal: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const name = String(i.name ?? "").trim();
      if (!name) throw new Error("name_required");
      const row: MealRow = {
        id: uuid(),
        name,
        eaten_on: clampDate(i.eaten_on),
        calories: clampCalories(i.calories),
        protein_g: clampMacro(i.protein_g),
        carbs_g: clampMacro(i.carbs_g),
        fat_g: clampMacro(i.fat_g),
        source: clampSource(i.source),
        slot: clampSlot(i.slot),
        note: String(i.note ?? "").trim(),
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      return db.insert(MEALS, row);
    },
    update_meal: ({ id, patch }) => {
      const p = { ...((patch ?? {}) as Record<string, unknown>) };
      if ("name" in p) p.name = String(p.name ?? "").trim();
      if ("eaten_on" in p) p.eaten_on = clampDate(p.eaten_on);
      if ("calories" in p) p.calories = clampCalories(p.calories);
      if ("protein_g" in p) p.protein_g = clampMacro(p.protein_g);
      if ("carbs_g" in p) p.carbs_g = clampMacro(p.carbs_g);
      if ("fat_g" in p) p.fat_g = clampMacro(p.fat_g);
      if ("source" in p) p.source = clampSource(p.source);
      if ("slot" in p) p.slot = clampSlot(p.slot);
      if ("note" in p) p.note = String(p.note ?? "").trim();
      db.update(MEALS, String(id), { ...p, updated_at: nowIso() });
      return null;
    },
    delete_meal: ({ id }) => {
      db.remove(MEALS, String(id));
      return null;
    },

    // ── Weights (one per date; re-log overwrites — mirrors db.rs upsert) ───────
    list_weights: ({ limit }) => {
      const rows = sortedWeights();
      const n = Number(limit);
      return Number.isFinite(n) && n > 0 ? rows.slice(0, n) : rows;
    },
    upsert_weight: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const v = Number(i.weight_kg);
      if (!Number.isFinite(v) || v <= 0 || v > 700) throw new Error("weight_invalid");
      const measured_on = clampDate(i.measured_on);
      const weight_kg = round1(v);
      const note = String(i.note ?? "").trim();
      const existing = (db.all(WEIGHTS) as WeightRow[]).find((w) => w.measured_on === measured_on);
      if (existing) {
        db.update(WEIGHTS, existing.id, { weight_kg, note });
        return { ...existing, weight_kg, note };
      }
      const row: WeightRow = { id: uuid(), measured_on, weight_kg, note, created_at: nowIso() };
      return db.insert(WEIGHTS, row);
    },
    delete_weight: ({ id }) => {
      db.remove(WEIGHTS, String(id));
      return null;
    },

    // ── Derived signals — faithful JS port of signals.rs::compute ─────────────
    compute_signals: ({ today }) => {
      const todayStr = clampDate(today);
      const todayDay = dateToUtcDay(todayStr);
      const meals = sortedMeals().filter((m) => dateToUtcDay(m.eaten_on) <= todayDay);
      const weights = sortedWeights();

      // Per-day totals.
      const byDay = new Map<string, { date: string; calories: number; protein_g: number; carbs_g: number; fat_g: number; meal_count: number }>();
      let firstLoggedOn: string | null = null;
      for (const m of meals) {
        const e = byDay.get(m.eaten_on) ?? { date: m.eaten_on, calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, meal_count: 0 };
        e.calories += m.calories;
        e.protein_g = round1(e.protein_g + (m.protein_g ?? 0));
        e.carbs_g = round1(e.carbs_g + (m.carbs_g ?? 0));
        e.fat_g = round1(e.fat_g + (m.fat_g ?? 0));
        e.meal_count += 1;
        byDay.set(m.eaten_on, e);
        if (firstLoggedOn === null || m.eaten_on < firstLoggedOn) firstLoggedOn = m.eaten_on;
      }

      // Streak ending today (or yesterday).
      const dayStr = (d: number) => new Date(d * 86400000).toISOString().slice(0, 10);
      let streak = 0;
      let cursor: number | null = byDay.has(todayStr) ? todayDay : byDay.has(dayStr(todayDay - 1)) ? todayDay - 1 : null;
      while (cursor !== null && byDay.has(dayStr(cursor))) { streak += 1; cursor -= 1; }

      const daily = [...byDay.values()].sort((a, b) => a.date.localeCompare(b.date));
      const daily14 = daily.filter((d) => dateToUtcDay(d.date) >= todayDay - 13);

      const win = (from: number, to: number) => {
        const rows = daily14.filter((d) => { const dd = dateToUtcDay(d.date); return dd >= from && dd <= to; });
        const days_logged = rows.length;
        const total_calories = rows.reduce((s, d) => s + d.calories, 0);
        const meal_count = rows.reduce((s, d) => s + d.meal_count, 0);
        const avg = (f: (d: typeof rows[number]) => number) =>
          rows.length ? round1(rows.reduce((s, d) => s + f(d), 0) / rows.length) : null;
        return {
          days_logged,
          total_calories,
          avg_calories_per_logged_day: days_logged > 0 ? Math.floor(total_calories / days_logged) : null,
          avg_protein_g: avg((d) => d.protein_g),
          avg_carbs_g: avg((d) => d.carbs_g),
          avg_fat_g: avg((d) => d.fat_g),
          meal_count,
        };
      };
      const this_week = win(todayDay - 6, todayDay);
      const prev_week = win(todayDay - 13, todayDay - 7);
      const wow =
        this_week.avg_calories_per_logged_day !== null && prev_week.avg_calories_per_logged_day !== null && prev_week.avg_calories_per_logged_day > 0
          ? round1(((this_week.avg_calories_per_logged_day - prev_week.avg_calories_per_logged_day) / prev_week.avg_calories_per_logged_day) * 100)
          : null;

      // Weekday pattern (≥2 samples per weekday, ≥100 kcal spread).
      const wdSum = new Array(7).fill(0); const wdN = new Array(7).fill(0);
      for (const d of daily) {
        const idx = utcDayToWeekdayIdx(dateToUtcDay(d.date));
        wdSum[idx] += d.calories; wdN[idx] += 1;
      }
      let best: [number, number] | null = null; let worst: [number, number] | null = null;
      for (let i = 0; i < 7; i++) {
        if (wdN[i] >= 2) {
          const avg = wdSum[i] / wdN[i];
          if (!best || avg > best[1]) best = [i, avg];
          if (!worst || avg < worst[1]) worst = [i, avg];
        }
      }
      let highest_avg_weekday: string | null = null; let lowest_avg_weekday: string | null = null;
      if (best && worst && best[0] !== worst[0] && best[1] - worst[1] >= 100) {
        highest_avg_weekday = WEEKDAYS[best[0]];
        lowest_avg_weekday = WEEKDAYS[worst[0]];
      }

      const source_mix = { manual: 0, photo: 0, label: 0, barcode: 0 };
      for (const m of meals) {
        if (m.source === "photo") source_mix.photo += 1;
        else if (m.source === "label") source_mix.label += 1;
        else if (m.source === "barcode") source_mix.barcode += 1;
        else source_mix.manual += 1;
      }

      const inWindow = weights.filter((w) => { const d = dateToUtcDay(w.measured_on); return d >= todayDay - 30 && d <= todayDay; });
      const latest = inWindow[0] ?? null;
      const earliest = inWindow[inWindow.length - 1] ?? null;
      const weight = {
        entry_count: weights.length,
        latest_kg: latest ? latest.weight_kg : null,
        latest_on: latest ? latest.measured_on : null,
        change_30d_kg: latest && earliest && latest.measured_on !== earliest.measured_on ? round1(latest.weight_kg - earliest.weight_kg) : null,
        earliest_in_window_on: earliest ? earliest.measured_on : null,
      };

      // Pre-baked observational facts (mirrors signals.rs wording).
      const facts: { kind: string; text: string; tone: string }[] = [];
      if (this_week.avg_calories_per_logged_day !== null) {
        facts.push({
          kind: "week_avg",
          text: `This week you logged ${this_week.meal_count} meal${this_week.meal_count === 1 ? "" : "s"} across ${this_week.days_logged} day${this_week.days_logged === 1 ? "" : "s"}, averaging about ${this_week.avg_calories_per_logged_day} kcal per logged day (estimates).`,
          tone: "neutral",
        });
      }
      if (wow !== null && Math.abs(wow) >= 5) {
        facts.push({
          kind: "week_delta",
          text: `Your average logged calories per day were ${Math.abs(wow)}% ${wow > 0 ? "higher" : "lower"} than the week before.`,
          tone: "neutral",
        });
      }
      if (streak >= 3) facts.push({ kind: "streak", text: `You've logged meals ${streak} days in a row.`, tone: "good" });
      if (highest_avg_weekday && lowest_avg_weekday) {
        facts.push({ kind: "weekday_pattern", text: `${highest_avg_weekday}s average the most logged calories in your history; ${lowest_avg_weekday}s the least.`, tone: "neutral" });
      }
      if (weight.change_30d_kg !== null && weight.latest_kg !== null && Math.abs(weight.change_30d_kg) >= 0.1) {
        facts.push({
          kind: "weight_30d",
          text: `Your logged weight moved ${weight.change_30d_kg > 0 ? "+" : ""}${weight.change_30d_kg} kg over the last 30 days of entries (latest ${weight.latest_kg} kg).`,
          tone: "neutral",
        });
      }
      if ((this_week.avg_protein_g ?? 0) > 0) {
        facts.push({
          kind: "macros_week",
          text: `On logged days this week your entries averaged roughly ${this_week.avg_protein_g}g protein, ${this_week.avg_carbs_g}g carbs and ${this_week.avg_fat_g}g fat (estimates).`,
          tone: "neutral",
        });
      }
      const assisted = source_mix.photo + source_mix.label + source_mix.barcode;
      if (assisted > 0 && meals.length > 0) {
        facts.push({
          kind: "source_mix",
          text: `${assisted} of your ${meals.length} meals were captured with an assist (photo, label scan, or barcode).`,
          tone: "neutral",
        });
      }

      return {
        schema:
          "oriora.health_tracker.v1-signals: derived aggregates over the user's OWN on-device food + weight log. All calorie/macro figures are the user's logged ESTIMATES. `headline_facts` are pre-baked, ready-to-say OBSERVATIONAL sentences already containing the figures — narrate them, never re-count or invent a number. Every field is a plain observation of what the user logged: never a diagnosis, a target, a diet, a recommendation, or a judgement of the person or their body.",
        generated_for: todayStr,
        total_meals: meals.length,
        distinct_days_logged: byDay.size,
        first_logged_on: firstLoggedOn,
        logging_streak_days: streak,
        daily_totals_14d: daily14,
        this_week,
        prev_week,
        week_over_week_calories_pct: wow,
        highest_avg_weekday,
        lowest_avg_weekday,
        source_mix,
        weight,
        headline_facts: facts,
      };
    },

    // ── Native-only (device-direct vision + on-device file store/OCR + on-device BYOK key).
    //    On web these degrade so the MealForm hides/falls back (the photo + label assists are
    //    hidden on web; barcode is plain client fetch and works everywhere).
    save_label_photo: () => "",
    ocr_image: () => "",
  };
}
